// src/components/Profile/EmployeePopup.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  User, Wallet, BarChart3, FileText, GraduationCap, CalendarOff, Lock, ShieldCheck, BookOpen,
  X, Upload, Trash2, Download, Plus, Send, KeyRound,
} from 'lucide-react';
import api from '../../apiClient';
import {
  listEmployees, getEmployee, listTraining, createTraining,
  listEmployeeDocs, uploadEmployeeDoc, deleteEmployeeDoc, DOC_CATEGORIES, docName, docUrl,
} from '../../api/teamApi';
import { approveTimeOff, denyTimeOff, setPtoAllowance, timeOffErrorMessage } from '../../api/timeOffApi';
import { getComplianceOverview, listTechnicianProfiles, techUserId, docType } from '../../api/teamApi';
import { listDocumentRequests, requestDocument, resendDocumentRequest, DOC_TYPES, docTypeLabel } from '../../api/onboardingApi';
import LicenseIntake from '../Compliance/LicenseIntake';
import { getUserRole } from '../../helpers/tenantHelpers';
import './EmployeePopup.css';

/* THE employee card — the only person surface in the app (founder decision
   2026-09-08: "the team directory popup IS the employee card"). Info / Pay /
   Commission / Docs / Skills / Time Off / Training / Compliance / Security.
   Docs = StoredFile owner_type=employee; Time Off, Skills, Training and
   Compliance read lane D's person join (/api/people/employees/<id>/) — one
   source, the same rows the Team tabs and the dispatch board read. */

// ── Constants ──

const ROLE_OPTIONS = [
  { value: 'Tech', label: 'Technician' },
  { value: 'Sales', label: 'Sales Rep' },
  { value: 'office_staff', label: 'Office Staff' },
  { value: 'Manager', label: 'Manager' },
  { value: 'Admin', label: 'Admin' },
  { value: 'owner', label: 'Owner' },
];

const COMPENSATION_TYPES = [
  { value: 'salary', label: 'Salary' },
  { value: 'hourly', label: 'Hourly' },
];

const TABS = [
  { key: 'info', label: 'Info', Icon: User },
  { key: 'pay', label: 'Pay', Icon: Wallet },
  { key: 'commission', label: 'Commission', Icon: BarChart3 },
  { key: 'docs', label: 'Docs', Icon: FileText },
  { key: 'skills', label: 'Skills', Icon: GraduationCap },
  { key: 'vacation', label: 'Time Off', Icon: CalendarOff },
  { key: 'training', label: 'Training', Icon: BookOpen },
  { key: 'compliance', label: 'Compliance', Icon: ShieldCheck },
  { key: 'security', label: 'Security', Icon: Lock },
];
const MANAGER_ROLES = new Set(['owner', 'admin', 'manager']);
const fmtDate = (v) => (v ? new Date(v + (String(v).length === 10 ? 'T00:00:00' : '')).toLocaleDateString() : '—');
const humanize = (v) => String(v || '—').replace(/_/g, ' ');

const normalizeUserRole = (value = '') => {
  const normalized = String(value || '').trim().toLowerCase();
  if (['admin', 'owner', 'superadmin'].includes(normalized)) return 'Admin';
  if (['manager', 'mgr'].includes(normalized)) return 'Manager';
  if (['sales', 'sales_rep', 'member'].includes(normalized)) return 'Sales';
  if (['office_staff', 'office'].includes(normalized)) return 'office_staff';
  if (['tech', 'technician', 'field_tech', 'instructor'].includes(normalized)) return 'Tech';
  return 'Tech';
};

const USER_UPDATE_ENDPOINTS = (userId) => [
  `/accounts/team/users/${userId}/`,
  `/accounts/users/${userId}/`,
];

const isEndpointFallbackStatus = (status) => status === 404 || status === 405;

/* The card is a window: drag it by the header, resize from the corner. A manager
   working a roster drags it aside and keeps the list visible. Size persists;
   position resets to centred on open. */
const BOX_KEY = 'crm.employeeCard.size';
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
function useWindowBox(panelRef) {
  const [box, setBox] = useState(null);
  const [dragging, setDragging] = useState(false);
  const op = useRef(null);
  const begin = useCallback((mode) => (e) => {
    if (e.button !== 0) return;
    if (mode === 'move' && e.target.closest('button, input, a, select, textarea')) return;
    const r = panelRef.current?.getBoundingClientRect();
    if (!r) return;
    op.current = { mode, sx: e.clientX, sy: e.clientY, x: r.left, y: r.top, w: r.width, h: r.height };
    setDragging(true);
    e.preventDefault();
  }, [panelRef]);
  useEffect(() => {
    const move = (e) => {
      const d = op.current; if (!d) return;
      const dx = e.clientX - d.sx; const dy = e.clientY - d.sy;
      if (d.mode === 'move') setBox({ x: clamp(d.x + dx, 0, window.innerWidth - 160), y: clamp(d.y + dy, 0, window.innerHeight - 48), w: d.w, h: d.h });
      else setBox({ x: d.x, y: d.y, w: clamp(d.w + dx, 520, window.innerWidth - d.x), h: clamp(d.h + dy, 360, window.innerHeight - d.y) });
    };
    const up = () => { if (!op.current) return; op.current = null; setDragging(false); };
    window.addEventListener('mousemove', move); window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, []);
  useEffect(() => { if (box) { try { localStorage.setItem(BOX_KEY, JSON.stringify({ w: Math.round(box.w), h: Math.round(box.h) })); } catch { /* private mode */ } } }, [box]);
  const saved = useMemo(() => { try { return JSON.parse(localStorage.getItem(BOX_KEY) || 'null'); } catch { return null; } }, []);
  const style = box ? { left: box.x, top: box.y, width: box.w, height: box.h }
    : saved?.w ? { width: Math.min(saved.w, window.innerWidth - 32), height: Math.min(saved.h, window.innerHeight - 32) } : undefined;
  return { style, placed: !!box, dragging, startMove: begin('move'), startResize: begin('resize') };
}

// ── Component ──

const EmployeePopup = ({ employee, onClose, onSave, initialTab = 'info' }) => {
  const [updatedEmployee, setUpdatedEmployee] = useState({
    ...employee,
    role: normalizeUserRole(employee?.role),
  });
  const [activeTab, setActiveTab] = useState(initialTab || 'info');
  const viewerRole = String(getUserRole('Member') || '').toLowerCase();
  const viewerIsManager = MANAGER_ROLES.has(viewerRole);
  const me = (() => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; } })();
  const viewerUserId = Number(me?.id ?? me?.user_id ?? me?.pk) || null;
  const [showFileInput, setShowFileInput] = useState(false);
  const panelRef = useRef(null);
  const win = useWindowBox(panelRef);
  const [avatarFile, setAvatarFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);

  // Security
  const [tempPassword, setTempPassword] = useState('');
  const [tempPasswordConfirm, setTempPasswordConfirm] = useState('');
  const [securityAction, setSecurityAction] = useState(null);
  const [securityLoading, setSecurityLoading] = useState(false);
  const [securityResult, setSecurityResult] = useState(null);

  // The person join (lane D): EmploymentProfile + credentials + licensed_for + skills + time_off + training + comp
  const [profile, setProfile] = useState(null);      // roster row {id, user_id, ...} or null when not on the roster
  const [join, setJoin] = useState(null);
  const [joinLoading, setJoinLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [ptoForm, setPtoForm] = useState(null); // {days_per_year, accrual, carryover, year_start} while editing
  const [ptoSaving, setPtoSaving] = useState(false);

  // Docs (StoredFile owner_type=employee)
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docCategory, setDocCategory] = useState('document');
  const [docUploading, setDocUploading] = useState(false);
  // Ad-hoc document requests (lane D's DocumentRequest): ask, they get a link, it lands here.
  const [docRequests, setDocRequests] = useState([]);
  const [reqForm, setReqForm] = useState(null); // {doc_type, note} while the form is open
  const [reqBusy, setReqBusy] = useState(false);
  const loadRequests = async (employeeId) => {
    if (!employeeId) { setDocRequests([]); return; }
    try { const r = await listDocumentRequests({ employee_id: employeeId }); setDocRequests(r.data?.requests || []); }
    catch { setDocRequests([]); }
  };
  const sendRequest = async () => {
    if (!profile || !reqForm?.doc_type) return;
    setReqBusy(true);
    try {
      const r = await requestDocument({ employee_id: profile.id, doc_type: reqForm.doc_type, note: reqForm.note || '' });
      const n = r.data?.notified || {};
      const how = [n.email === 'sent' ? 'emailed' : n.email === 'no_address' ? 'no email on file' : n.email ? `email ${n.email}` : null,
        n.push === 'sent' ? 'pushed to their phone' : n.push === 'no_device' ? 'no app device' : n.push ? `push ${n.push}` : null].filter(Boolean).join(' · ');
      setSaveResult({ type: n.email === 'sent' || n.push === 'sent' ? 'success' : 'error', message: `Asked for ${docTypeLabel(reqForm.doc_type)} — ${how || 'link created, nothing delivered'}` });
      setReqForm(null); await loadRequests(profile.id);
    } catch (err) { setSaveResult({ type: 'error', message: err?.payload?.detail || 'Couldn’t send the request.' }); }
    finally { setReqBusy(false); }
  };

  // Compliance intake (document-first): requirement types + this person's technician profile id
  const [reqTypes, setReqTypes] = useState([]);
  const [techId, setTechId] = useState(null);
  const [intakeType, setIntakeType] = useState('');
  const [intakeOpen, setIntakeOpen] = useState(false);
  useEffect(() => {
    let live = true;
    getComplianceOverview().then((r) => { if (!live) return; const reqs = r.data?.requirements?.requirements || r.data?.requirements || []; setReqTypes(Array.isArray(reqs) ? reqs.filter((q) => q.holder_level === 'employee') : []); }).catch(() => {});
    listTechnicianProfiles().then((r) => { if (!live) return; const list = Array.isArray(r.data) ? r.data : r.data?.results || []; const t = list.find((x) => techUserId(x) === Number(employee?.id)); setTechId(t ? t.id : null); }).catch(() => {});
    return () => { live = false; };
  }, [employee?.id]);

  // Training
  const [training, setTraining] = useState([]);
  const [trainingForm, setTrainingForm] = useState({ course: '', completed_on: '', hours: '', ceu_credits: '' });
  const [trainingSaving, setTrainingSaving] = useState(false);

  // ── Load compensation ──
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const response = await api.get('/analytics/cost-configuration/');
        const profiles = Array.isArray(response?.data?.compensation_profiles)
          ? response.data.compensation_profiles : [];
        const match = profiles.find((row) => Number(row?.user_id) === Number(employee?.id));
        if (!mounted || !match) return;
        setUpdatedEmployee((prev) => ({
          ...prev,
          salary: match?.monthly_salary ?? prev.salary ?? '',
          hourly_rate: match?.hourly_rate ?? prev.hourly_rate ?? '',
          commissionRate: match?.commission_rate ?? prev.commissionRate ?? '',
          compensation_type: match?.compensation_type || prev.compensation_type || 'salary',
          compensation_notes: match?.notes || prev.compensation_notes || '',
        }));
      } catch { /* non-critical */ }
    })();
    return () => { mounted = false; };
  }, [employee?.id]);

  // ── Load the person join ──
  const loadJoin = async () => {
    setJoinLoading(true);
    try {
      const r = await listEmployees();
      const row = (r.data?.employees || []).find((e) => Number(e.user_id) === Number(employee?.id)) || null;
      setProfile(row);
      if (row) {
        const j = await getEmployee(row.id); setJoin(j.data || null);
        try { const t = await listTraining({ employee_id: row.id }); setTraining(t.data?.training || []); } catch { setTraining([]); }
        loadRequests(row.id);
      } else { setJoin(null); setTraining([]); setDocRequests([]); }
    } catch { setProfile(null); setJoin(null); }
    finally { setJoinLoading(false); }
  };
  useEffect(() => { loadJoin(); }, [employee?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load documents ──
  const loadDocs = async () => {
    if (!employee?.id) return;
    setDocsLoading(true);
    try { const r = await listEmployeeDocs(employee.id); const items = Array.isArray(r.data?.results) ? r.data.results : Array.isArray(r.data) ? r.data : r.data?.files || []; setDocs(items); }
    catch { setDocs([]); }
    finally { setDocsLoading(false); }
  };
  useEffect(() => { loadDocs(); }, [employee?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUpdatedEmployee((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleProfilePicChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      setUpdatedEmployee((prev) => ({ ...prev, avatar: URL.createObjectURL(file) }));
    }
  };

  // ── Save employee ──
  const saveEmployeeChanges = async () => {
    setSaving(true);
    setSaveResult(null);
    try {
      const formData = new FormData();
      formData.append('first_name', updatedEmployee.first_name || updatedEmployee.firstName || '');
      formData.append('last_name', updatedEmployee.last_name || updatedEmployee.lastName || '');
      formData.append('email', updatedEmployee.email || '');
      formData.append('phone_number', updatedEmployee.phone_number || '');
      formData.append('role', updatedEmployee.role || 'Tech');
      formData.append('is_active', updatedEmployee.is_active !== false ? 'true' : 'false');
      formData.append('external_sales_rep_id', updatedEmployee.external_sales_rep_id || '');
      formData.append('twilio_phone', updatedEmployee.twilio_phone || '');
      formData.append('call_forwarding', updatedEmployee.call_forwarding || '');
      if (avatarFile) formData.append('avatar', avatarFile);

      let response = null;
      let lastError = null;
      for (const endpoint of USER_UPDATE_ENDPOINTS(updatedEmployee.id)) {
        try {
          response = await api.patch(endpoint, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          break;
        } catch (err) {
          lastError = err;
          if (isEndpointFallbackStatus(err?.response?.status)) continue;
          break;
        }
      }
      if (!response) throw lastError || new Error('Unable to update team member.');

      try {
        await api.patch(`/analytics/compensation-profiles/${updatedEmployee.id}/`, {
          compensation_type: updatedEmployee.compensation_type || 'salary',
          monthly_salary: updatedEmployee.salary || null,
          hourly_rate: updatedEmployee.hourly_rate || null,
          commission_rate: updatedEmployee.commissionRate || null,
          notes: updatedEmployee.compensation_notes || '',
          is_active: true,
        });
      } catch { /* non-critical */ }

      setSaveResult({ type: 'success', message: 'Employee details saved.' });
      if (onSave) onSave(response.data);
    } catch (error) {
      setSaveResult({ type: 'error', message: error?.response?.data?.detail || error?.message || 'Failed to save.' });
    } finally {
      setSaving(false);
    }
  };

  // ── Security: password reset ──
  const handleSendPasswordReset = async () => {
    setSecurityLoading(true);
    setSecurityResult(null);
    try {
      const res = await api.post(`/accounts/team/${updatedEmployee.id}/send-password-reset/`);
      setSecurityResult({ type: 'success', message: res?.data?.detail || 'Reset code sent.' });
    } catch (err) {
      setSecurityResult({ type: 'error', message: err?.response?.data?.detail || 'Failed to send.' });
    } finally {
      setSecurityLoading(false);
    }
  };

  // ── Security: temp password ──
  const handleSetTempPassword = async () => {
    if (!tempPassword || tempPassword.length < 8) {
      setSecurityResult({ type: 'error', message: 'Min 8 characters.' }); return;
    }
    if (tempPassword !== tempPasswordConfirm) {
      setSecurityResult({ type: 'error', message: 'Passwords do not match.' }); return;
    }
    if (!/[A-Z]/.test(tempPassword)) {
      setSecurityResult({ type: 'error', message: 'Need at least one uppercase letter.' }); return;
    }
    if (!/\d/.test(tempPassword)) {
      setSecurityResult({ type: 'error', message: 'Need at least one number.' }); return;
    }
    setSecurityLoading(true);
    setSecurityResult(null);
    try {
      const res = await api.post(`/accounts/team/${updatedEmployee.id}/set-temporary-password/`, { password: tempPassword });
      setSecurityResult({ type: 'success', message: res?.data?.detail || 'Password set.' });
      setTempPassword(''); setTempPasswordConfirm(''); setSecurityAction(null);
    } catch (err) {
      setSecurityResult({ type: 'error', message: err?.response?.data?.detail || 'Failed.' });
    } finally {
      setSecurityLoading(false);
    }
  };

  // ── Docs ──
  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setDocUploading(true);
    try { await uploadEmployeeDoc(employee.id, file, docCategory); await loadDocs(); }
    catch (err) { alert(err?.response?.data?.detail || err?.payload?.detail || 'Upload failed.'); }
    finally { setDocUploading(false); e.target.value = ''; }
  };
  const handleDocDelete = async (f) => {
    if (!window.confirm(`Remove ${docName(f)}?`)) return;
    try { await deleteEmployeeDoc(f.file_uuid || f.uuid || f.id); await loadDocs(); }
    catch (err) { alert(err?.response?.data?.detail || 'Couldn’t remove it.'); }
  };

  // ── Time off decisions (manager) ──
  const decide = async (id, fn, label, verb) => {
    setBusyId(id);
    try { await fn(id); setSaveResult({ type: 'success', message: label }); await loadJoin(); }
    catch (err) { setSaveResult({ type: 'error', message: timeOffErrorMessage(err, verb) }); }
    finally { setBusyId(null); }
  };

  // PTO allowance on the employment record — null = "PTO not set", never 0.
  const ptoAllowance = join?.time_off?.allowance || null;
  const openPtoForm = () => setPtoForm({
    days_per_year: ptoAllowance?.days_per_year ?? '',
    accrual: ptoAllowance?.accrual || 'annual_grant',
    carryover: !!ptoAllowance?.carryover,
    year_start: ptoAllowance?.year_start || `${new Date().getFullYear()}-01-01`,
  });
  const savePto = async () => {
    if (!profile?.id) return;
    setPtoSaving(true);
    try {
      const days = ptoForm.days_per_year === '' ? null : Number(ptoForm.days_per_year);
      await setPtoAllowance(profile.id, { ...ptoForm, days_per_year: days });
      setSaveResult({ type: 'success', message: days == null ? 'PTO allowance cleared' : 'PTO allowance saved' });
      setPtoForm(null); await loadJoin();
    } catch (err) {
      setSaveResult({ type: 'error', message: err?.payload?.detail || (Number(err?.status) >= 500 ? `The server hit an error and didn’t save the allowance (HTTP ${err.status}).` : 'Couldn’t reach the server to save the allowance.') });
    } finally { setPtoSaving(false); }
  };

  // ── Training: record ──
  const handleAddTraining = async () => {
    if (!profile || !trainingForm.course.trim()) return;
    setTrainingSaving(true);
    try {
      await createTraining({ employee_id: profile.id, course: trainingForm.course.trim(), completed_on: trainingForm.completed_on || null, hours: trainingForm.hours || null, ceu_credits: trainingForm.ceu_credits || 0 });
      setTrainingForm({ course: '', completed_on: '', hours: '', ceu_credits: '' }); await loadJoin();
    } catch (err) { setSaveResult({ type: 'error', message: err?.payload?.detail || err?.payload?.error || 'Couldn’t record training.' }); }
    finally { setTrainingSaving(false); }
  };

  const skills = join?.skills || [];
  const credentials = join?.credentials || [];
  const licensedFor = join?.licensed_for || [];
  const timeOff = join?.time_off?.requests || [];
  const isSelf = viewerUserId && Number(employee?.id) === viewerUserId;
  const NotOnRoster = () => (
    <div className="empty-state">
      <User size={32} className="empty-icon-svg" />
      <p>Not on the employee roster yet.</p>
      <p className="empty-hint">Time off, training, credentials and skills resolve to an employment record. One is created by the roster backfill or when this person is set up under Team.</p>
    </div>
  );

  const firstName = updatedEmployee.first_name || updatedEmployee.firstName || '';
  const lastName = updatedEmployee.last_name || updatedEmployee.lastName || '';
  const roleBadge = normalizeUserRole(updatedEmployee.role);
  const roleLabel = (ROLE_OPTIONS.find((o) => o.value === roleBadge) || {}).label || roleBadge;
  const isActive = updatedEmployee.is_active !== false;
  const editable = ['info', 'pay', 'commission'].includes(activeTab);

  return (
    <div className="popup-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={panelRef} className={`popup employee-popup ${win.placed ? 'is-placed' : ''} ${win.dragging ? 'is-dragging' : ''}`} style={win.style} role="dialog" aria-label={`${firstName} ${lastName}`.trim()}>

        {/* Header — one line: avatar · name · role · status · email. Drag handle. */}
        <div className="ep-head" onMouseDown={win.startMove}>
          <div className="ep-avatar" onClick={() => setShowFileInput(!showFileInput)} title="Change photo">
            {updatedEmployee.avatar ? <img src={updatedEmployee.avatar} alt="" /> : <span>{(firstName[0] || '').toUpperCase()}{(lastName[0] || '').toUpperCase()}</span>}
          </div>
          <div className="ep-id">
            <span className="ep-name">{firstName} {lastName}</span>
            <span className="ep-pill role">{roleLabel}</span>
            <span className={`ep-pill ${isActive ? 'on' : 'off'}`}><span className="dot" aria-hidden />{isActive ? 'Active' : 'Inactive'}</span>
            {updatedEmployee.email ? <span className="ep-email" title={updatedEmployee.email}>{updatedEmployee.email}</span> : null}
          </div>
          <button type="button" className="ep-close" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>
        {showFileInput && (
          <div className="ep-avatar-row"><input type="file" accept="image/*" onChange={handleProfilePicChange} className="avatar-file-input" /></div>
        )}

        {/* Tabs — directly under the header */}
        <nav className="ep-tabs" aria-label="Employee">
          {TABS.map((tab) => (
            <button key={tab.key} type="button"
              className={activeTab === tab.key ? 'active' : ''}
              onClick={() => { setActiveTab(tab.key); setSaveResult(null); setSecurityResult(null); }}
            >
              <tab.Icon size={14} className="tab-icon-svg" />
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </nav>

        {/* Body */}
        <div className="ep-body">
          {saveResult && (
            <div className={`result-banner result-${saveResult.type}`} onClick={() => setSaveResult(null)}>
              {saveResult.message}
            </div>
          )}

          {/* ═══ INFO ═══ */}
          {activeTab === 'info' && (
            <section className="tab-section">
              <div className="form-grid-2">
                <div className="popup-form-group">
                  <label>First Name</label>
                  <input type="text" name="first_name" value={firstName} onChange={handleChange} />
                </div>
                <div className="popup-form-group">
                  <label>Last Name</label>
                  <input type="text" name="last_name" value={lastName} onChange={handleChange} />
                </div>
              </div>
              <div className="form-grid-2">
                <div className="popup-form-group">
                  <label>Email</label>
                  <input type="email" name="email" value={updatedEmployee.email || ''} onChange={handleChange} />
                </div>
                <div className="popup-form-group">
                  <label>Phone</label>
                  <input type="text" name="phone_number" value={updatedEmployee.phone_number || ''} onChange={handleChange} />
                </div>
              </div>
              <div className="popup-form-group">
                <label>Address</label>
                <input type="text" name="address" value={updatedEmployee.address || ''} onChange={handleChange} />
              </div>
              <div className="form-grid-2">
                <div className="popup-form-group">
                  <label>Role</label>
                  <select name="role" value={updatedEmployee.role || 'Tech'} onChange={handleChange}>
                    {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="popup-form-group">
                  <label>Status</label>
                  <select name="is_active" value={isActive ? 'true' : 'false'}
                    onChange={(e) => setUpdatedEmployee((p) => ({ ...p, is_active: e.target.value === 'true' }))}>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="form-grid-2">
                <div className="popup-form-group">
                  <label>Twilio Phone</label>
                  <input type="text" name="twilio_phone" value={updatedEmployee.twilio_phone || ''} onChange={handleChange} />
                </div>
                <div className="popup-form-group">
                  <label>Call Forwarding</label>
                  <input type="text" name="call_forwarding" value={updatedEmployee.call_forwarding || ''} onChange={handleChange} />
                </div>
              </div>
              <div className="popup-form-group">
                <label>External Sales Rep ID</label>
                <input type="text" name="external_sales_rep_id" value={updatedEmployee.external_sales_rep_id || ''} onChange={handleChange} />
              </div>
            </section>
          )}

          {/* ═══ PAY ═══ */}
          {activeTab === 'pay' && (
            <section className="tab-section">
              <div className="form-grid-2">
                <div className="popup-form-group">
                  <label>Pay Type</label>
                  <select name="compensation_type" value={updatedEmployee.compensation_type || 'salary'} onChange={handleChange}>
                    {COMPENSATION_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="popup-form-group">
                  <label>{(updatedEmployee.compensation_type || 'salary') === 'hourly' ? 'Hourly Rate ($)' : 'Monthly Salary ($)'}</label>
                  <input type="number" step="0.01"
                    name={(updatedEmployee.compensation_type || 'salary') === 'hourly' ? 'hourly_rate' : 'salary'}
                    value={(updatedEmployee.compensation_type || 'salary') === 'hourly' ? (updatedEmployee.hourly_rate || '') : (updatedEmployee.salary || '')}
                    onChange={handleChange} />
                </div>
              </div>
              <div className="popup-form-group">
                <label>Payroll Notes</label>
                <textarea rows="3" name="compensation_notes" value={updatedEmployee.compensation_notes || ''} onChange={handleChange}
                  placeholder="Overtime eligibility, bonus structure, etc." />
              </div>
            </section>
          )}

          {/* ═══ COMMISSION ═══ */}
          {activeTab === 'commission' && (
            <section className="tab-section">
              <div className="form-grid-2">
                <div className="popup-form-group">
                  <label>Commission Rate (%)</label>
                  <input type="number" step="0.1" name="commissionRate" value={updatedEmployee.commissionRate || ''} onChange={handleChange} />
                </div>
                <div className="popup-form-group">
                  <label>Commission Basis</label>
                  <input type="text" name="commission_basis" value={updatedEmployee.commission_basis || ''} onChange={handleChange}
                    placeholder="e.g. Revenue, Profit, Per Job" />
                </div>
              </div>
              {/* TODO: Wire to GET /api/analytics/commissions/?user={id} when CommissionLedger model exists */}
              <div className="empty-state">
                <BarChart3 size={32} className="empty-icon-svg" />
                <p>Commission ledger not configured yet.</p>
                <p className="empty-hint">Earnings tracking will appear here once enabled.</p>
              </div>
            </section>
          )}

          {/* ═══ DOCS ═══ */}
          {activeTab === 'docs' && (
            <section className="tab-section">
              <div className="form-grid-3">
                <div className="popup-form-group">
                  <label>Type</label>
                  <select value={docCategory} onChange={(e) => setDocCategory(e.target.value)}>
                    {DOC_CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </div>
                <div className="popup-form-group">
                  <label>Upload</label>
                  <input type="file" onChange={handleDocUpload} disabled={docUploading} />
                </div>
                {viewerIsManager && profile && (
                  <div className="popup-form-group">
                    <label>Or ask them for it</label>
                    <button type="button" className="action-btn action-primary" onClick={() => setReqForm(reqForm ? null : { doc_type: 'drivers_license', note: '' })}><Send size={14} /> {reqForm ? 'Cancel' : 'Request a document'}</button>
                  </div>
                )}
              </div>
              {reqForm && (
                <div className="temp-password-form">
                  <div className="form-grid-2">
                    <div className="popup-form-group"><label>Document</label>
                      <select value={reqForm.doc_type} onChange={(e) => setReqForm({ ...reqForm, doc_type: e.target.value })}>{DOC_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
                    </div>
                    <div className="popup-form-group"><label>Note (optional)</label><input type="text" value={reqForm.note} onChange={(e) => setReqForm({ ...reqForm, note: e.target.value })} placeholder="e.g. front and back" /></div>
                  </div>
                  <div className="temp-password-actions">
                    <button type="button" className="action-btn action-save" disabled={reqBusy} onClick={sendRequest}>{reqBusy ? 'Sending…' : 'Send the request'}</button>
                    <span className="tab-hint">They get a link by email and push; the upload lands here.</span>
                  </div>
                </div>
              )}
              {docRequests.filter((r) => r.status === 'requested').length > 0 && (
                <div className="time-off-list">
                  {docRequests.filter((r) => r.status === 'requested').map((r) => (
                    <div key={`req-${r.id}`} className="time-off-entry">
                      <div className="time-off-info">
                        <span className="time-off-dates">{docTypeLabel(r.doc_type)}</span>
                        <span className="time-off-reason"><span className="time-off-cat">requested</span>{r.note || 'waiting on their upload'}</span>
                      </div>
                      {viewerIsManager && <button type="button" className="action-btn action-neutral" onClick={async () => { try { const s = await resendDocumentRequest(r.id); const n = s.data?.notified || {}; setSaveResult({ type: 'success', message: `Reminder sent — email ${n.email || '—'}, push ${n.push || '—'}` }); } catch { setSaveResult({ type: 'error', message: 'Couldn’t resend.' }); } }}>Remind</button>}
                    </div>
                  ))}
                </div>
              )}
              {docsLoading ? <p className="tab-hint">Loading…</p>
                : docs.length === 0 ? (
                  <div className="empty-state"><FileText size={28} className="empty-icon-svg" /><p>No documents on file.</p><p className="empty-hint">Tax forms, licenses, agreements, certifications — stored against this person. Onboarding uploads land here too.</p></div>
                ) : (
                  <div className="time-off-list">
                    {docs.map((f) => (
                      <div key={f.file_uuid || f.uuid || f.id} className="time-off-entry">
                        <div className="time-off-info">
                          <span className="time-off-dates">{docName(f)}</span>
                          <span className="time-off-reason">{docType(f) ? <span className="time-off-cat">{docTypeLabel(docType(f))}</span> : null}{f.created_at ? fmtDate(String(f.created_at).slice(0, 10)) : ''}</span>
                        </div>
                        {docUrl(f) && <a className="icon-btn" href={docUrl(f)} target="_blank" rel="noreferrer" title="Open"><Download size={14} /></a>}
                        {viewerIsManager && <button className="icon-btn icon-btn-danger" onClick={() => handleDocDelete(f)} title="Remove"><Trash2 size={14} /></button>}
                      </div>
                    ))}
                  </div>
                )}
            </section>
          )}

          {/* ═══ SKILLS ═══ */}
          {activeTab === 'skills' && (
            <section className="tab-section">
              {joinLoading ? <p className="tab-hint">Loading…</p> : !profile ? <NotOnRoster /> : (
                <>
                  <div className="skills-list">
                    <label>Licensed for</label>
                    {licensedFor.length ? <div className="skill-chips">{licensedFor.map((c) => <span key={c} className="skill-chip skill-chip-active">{humanize(c)}</span>)}</div> : <p className="tab-hint">No active credential gates a service category.</p>}
                  </div>
                  <div className="skills-list">
                    <label>Technician skills</label>
                    {skills.length ? <div className="skill-chips">{skills.map((k) => <span key={k} className="skill-chip skill-chip-catalog">{humanize(k)}</span>)}</div> : <p className="tab-hint">No skills on the technician profile.</p>}
                    <p className="tab-hint">Read-only from the technician profile. On this tenant every field tech carries the same set, so there is nothing to edit until service history tags the vocabulary.</p>
                  </div>
                </>
              )}
            </section>
          )}

          {/* ═══ TIME OFF ═══ */}
          {activeTab === 'vacation' && (
            <section className="tab-section">
              {joinLoading ? <p className="tab-hint">Loading…</p> : !profile ? <NotOnRoster /> : (
                <>
                  <div className="pto-allowance">
                    {ptoAllowance
                      ? <p className="tab-hint"><strong>{ptoAllowance.days_left} day{ptoAllowance.days_left === 1 ? '' : 's'} left</strong> · {ptoAllowance.used_days} approved of {ptoAllowance.accrued_days} accrued{ptoAllowance.carry_in_days ? ` + ${ptoAllowance.carry_in_days} carried` : ''}{ptoAllowance.pending_days ? ` · ${ptoAllowance.pending_days} pending` : ''} · {ptoAllowance.days_per_year} days/yr, {ptoAllowance.accrual === 'monthly' ? 'monthly' : 'annual grant'}, carryover {ptoAllowance.carryover ? 'yes' : 'no'}, year starts {fmtDate(ptoAllowance.year_start)}</p>
                      : <p className="tab-hint"><strong>PTO not set</strong> — no allowance on this employment record, so there’s no balance yet.</p>}
                    {viewerIsManager && !ptoForm && <button type="button" className="action-btn action-neutral" onClick={openPtoForm}>{ptoAllowance ? 'Edit PTO allowance' : 'Set PTO allowance'}</button>}
                    {viewerIsManager && ptoForm && (
                      <div className="popup-form-grid">
                        <div className="popup-form-group"><label>PTO days per year</label><input type="number" min="0" max="366" step="0.5" value={ptoForm.days_per_year} onChange={(e) => setPtoForm({ ...ptoForm, days_per_year: e.target.value })} placeholder="e.g. 10" /></div>
                        <div className="popup-form-group"><label>Accrual</label><select value={ptoForm.accrual} onChange={(e) => setPtoForm({ ...ptoForm, accrual: e.target.value })}><option value="annual_grant">Annual grant (all at year start)</option><option value="monthly">Monthly (1/12 each month)</option></select></div>
                        <div className="popup-form-group"><label>Year starts</label><input type="date" value={ptoForm.year_start} onChange={(e) => setPtoForm({ ...ptoForm, year_start: e.target.value })} /></div>
                        <div className="popup-form-group"><label><input type="checkbox" checked={ptoForm.carryover} onChange={(e) => setPtoForm({ ...ptoForm, carryover: e.target.checked })} /> Unused days carry over</label></div>
                        <div className="popup-form-group"><button type="button" className="action-btn action-primary" disabled={ptoSaving} onClick={savePto}>{ptoSaving ? 'Saving…' : 'Save'}</button> <button type="button" className="action-btn action-neutral" onClick={() => setPtoForm(null)}>Cancel</button></div>
                      </div>
                    )}
                  </div>
                  <p className="tab-hint">Requests are filed by the employee (Team → Time off); managers approve here or in the queue. Approval blocks the schedule automatically; past dates can still be approved and count against PTO.</p>
                  {timeOff.length === 0 ? <p className="tab-hint">No requests on record.</p> : (
                    <div className="time-off-list">
                      {timeOff.map((t) => (
                        <div key={t.id} className="time-off-entry">
                          <div className="time-off-info">
                            <span className="time-off-dates">{fmtDate(t.start_date)} &mdash; {fmtDate(t.end_date)}{t.hours ? ` · ${t.hours} h` : ''}</span>
                            <span className="time-off-reason"><span className="time-off-cat">{humanize(t.category)}</span>{t.reason || ''}</span>
                            {t.reviewed_at && <span className="time-off-reason">{t.status === 'approved' ? 'Approved' : t.status === 'denied' ? 'Denied' : 'Reviewed'} by {t.reviewed_by_name || 'unknown'} on {new Date(t.reviewed_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}{t.review_note ? ` · “${t.review_note}”` : ''}</span>}
                          </div>
                          {t.status === 'pending' && t.end_date && t.end_date < new Date().toLocaleDateString('en-CA') && <span className="badge badge-inactive" title="These dates have passed; approving records it and counts it against PTO.">Past</span>}
                          <span className={`badge badge-${t.status === 'approved' ? 'active' : t.status === 'pending' ? 'role' : 'inactive'}`}>{t.status}</span>
                          {t.status === 'pending' && viewerIsManager && (
                            <>
                              <button className="action-btn action-primary" disabled={busyId === t.id} onClick={() => decide(t.id, approveTimeOff, 'Approved', 'approve')}>Approve</button>
                              <button className="action-btn action-neutral" disabled={busyId === t.id} onClick={() => decide(t.id, denyTimeOff, 'Denied', 'deny')}>Deny</button>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {isSelf && <p className="tab-hint">To request time off for yourself, use Team → Time off.</p>}
                </>
              )}
            </section>
          )}

          {/* ═══ TRAINING ═══ */}
          {activeTab === 'training' && (
            <section className="tab-section">
              {joinLoading ? <p className="tab-hint">Loading…</p> : !profile ? <NotOnRoster /> : (
                <>
                  {training.length === 0 ? <p className="tab-hint">No course completions recorded.</p> : (
                    <div className="time-off-list">
                      {training.map((r) => (
                        <div key={r.id} className="time-off-entry">
                          <div className="time-off-info">
                            <span className="time-off-dates">{r.course}</span>
                            <span className="time-off-reason">{fmtDate(r.completed_on)}{r.hours ? ` · ${r.hours} h` : ''}{Number(r.ceu_credits) > 0 ? ` · ${r.ceu_credits} CEU` : ''}{r.credential_id ? ' · counts toward a credential' : ''}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {viewerIsManager && (
                    <>
                      <div className="form-grid-4">
                        <div className="popup-form-group"><label>Course</label><input type="text" value={trainingForm.course} onChange={(e) => setTrainingForm((f) => ({ ...f, course: e.target.value }))} placeholder="e.g. FDACS CEU — L&O" /></div>
                        <div className="popup-form-group"><label>Completed</label><input type="date" value={trainingForm.completed_on} onChange={(e) => setTrainingForm((f) => ({ ...f, completed_on: e.target.value }))} /></div>
                        <div className="popup-form-group"><label>Hours</label><input type="number" min="0" step="0.5" value={trainingForm.hours} onChange={(e) => setTrainingForm((f) => ({ ...f, hours: e.target.value }))} /></div>
                        <div className="popup-form-group"><label>CEU</label><input type="number" min="0" step="0.5" value={trainingForm.ceu_credits} onChange={(e) => setTrainingForm((f) => ({ ...f, ceu_credits: e.target.value }))} /></div>
                      </div>
                      <button className="action-btn action-primary" onClick={handleAddTraining} disabled={trainingSaving || !trainingForm.course.trim()}><Plus size={14} /> {trainingSaving ? 'Saving…' : 'Record completion'}</button>
                    </>
                  )}
                </>
              )}
            </section>
          )}

          {/* ═══ COMPLIANCE ═══ */}
          {activeTab === 'compliance' && (
            <section className="tab-section">
              {joinLoading ? <p className="tab-hint">Loading…</p> : !profile ? <NotOnRoster /> : (
                <>
                  {credentials.length === 0 ? (
                    <div className="empty-state"><ShieldCheck size={32} className="empty-icon-svg" /><p>No credentials on file for this person.</p><p className="empty-hint">Upload the license PDF below; the details are read from the document and confirmed before saving.</p></div>
                  ) : (
                    <div className="time-off-list">
                      {credentials.map((c) => (
                        <div key={c.id} className="time-off-entry">
                          <div className="time-off-info">
                            <span className="time-off-dates">{c.label || humanize(c.type_key)}</span>
                            <span className="time-off-reason">{c.number ? `#${c.number} · ` : ''}{c.expiry_date ? `expires ${fmtDate(c.expiry_date)}` : 'no expiry on file'}</span>
                          </div>
                          <span className={`badge badge-${c.status === 'active' ? 'active' : c.status === 'expiring' ? 'role' : 'inactive'}`}>{humanize(c.status)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {licensedFor.length > 0 && <p className="tab-hint">Licensed for: {licensedFor.map(humanize).join(', ')}</p>}
                  {viewerIsManager && (
                    <div className="skills-list">
                      <label>Add a license from its PDF</label>
                      {techId == null ? <p className="tab-hint">This person has no technician profile, so an employee-held credential can’t be attached yet.</p>
                        : reqTypes.length === 0 ? <p className="tab-hint">No employee-held credential types in this tenant’s requirement pack (Settings → Compliance).</p>
                        : !intakeOpen ? (
                          <div className="form-grid-2">
                            <div className="popup-form-group"><label>Type</label>
                              <select value={intakeType} onChange={(e) => setIntakeType(e.target.value)}><option value="">Select…</option>{reqTypes.map((q) => <option key={q.type_key} value={q.type_key}>{q.label || q.type_key}</option>)}</select>
                            </div>
                            <div className="popup-form-group"><label>&nbsp;</label><button className="action-btn action-primary" disabled={!intakeType} onClick={() => setIntakeOpen(true)}><Upload size={14} /> Upload PDF</button></div>
                          </div>
                        ) : (
                          <LicenseIntake req={reqTypes.find((q) => q.type_key === intakeType)} tech={{ tech_id: techId, name: `${firstName} ${lastName}`.trim() }} existing={credentials.find((c) => c.type_key === intakeType) || null}
                            onSaved={async () => { setIntakeOpen(false); await loadJoin(); }} onCancel={() => setIntakeOpen(false)} />
                        )}
                    </div>
                  )}
                  <p className="tab-hint">SOP acknowledgments live on each SOP (Operations → SOPs → Acknowledgments).</p>
                </>
              )}
            </section>
          )}

          {/* ═══ SECURITY ═══ */}
          {activeTab === 'security' && (
            <section className="tab-section">
              <p className="tab-hint">Login credentials. Admin, manager and owner only.</p>

              {securityResult && (
                <div className={`result-banner result-${securityResult.type}`} onClick={() => setSecurityResult(null)}>
                  {securityResult.message}
                </div>
              )}

              <div className="security-actions">
                <button className="action-btn action-primary" onClick={handleSendPasswordReset} disabled={securityLoading}>
                  <Send size={14} /> {securityLoading ? 'Sending...' : 'Send Password Reset Email'}
                </button>
                <button className="action-btn action-warning"
                  onClick={() => setSecurityAction(securityAction === 'temp' ? null : 'temp')} disabled={securityLoading}>
                  <KeyRound size={14} /> Set Temporary Password
                </button>
              </div>

              {securityAction === 'temp' && (
                <div className="temp-password-form">
                  <div className="popup-form-group">
                    <label>New Password</label>
                    <input type="password" value={tempPassword} onChange={(e) => setTempPassword(e.target.value)}
                      placeholder="Min 8 chars, 1 uppercase, 1 number" autoComplete="new-password" />
                  </div>
                  <div className="popup-form-group">
                    <label>Confirm Password</label>
                    <input type="password" value={tempPasswordConfirm} onChange={(e) => setTempPasswordConfirm(e.target.value)}
                      placeholder="Re-enter password" autoComplete="new-password" />
                  </div>
                  <div className="temp-password-actions">
                    <button className="action-btn action-primary" onClick={handleSetTempPassword} disabled={securityLoading || !tempPassword}>
                      {securityLoading ? 'Setting...' : 'Set Password'}
                    </button>
                    <button className="action-btn action-neutral"
                      onClick={() => { setSecurityAction(null); setTempPassword(''); setTempPasswordConfirm(''); }}>
                      Cancel
                    </button>
                  </div>
                  <p className="tab-hint">User will be required to change this on next login.</p>
                </div>
              )}
            </section>
          )}
        </div>

        {/* Footer — Save only where there is something to save */}
        <div className="ep-foot">
          <button type="button" className="action-btn action-cancel" onClick={onClose}>Close</button>
          {editable && (
            <button type="button" className="action-btn action-save" onClick={saveEmployeeChanges} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          )}
        </div>
        <div className="ep-resize" onMouseDown={win.startResize} aria-hidden />
      </div>
    </div>
  );
};

export default EmployeePopup;
